import React from 'react';
import { GetStaticProps, GetStaticPaths } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import client from '../../lib/apollo';
import { GET_POST_BY_SLUG, GET_ALL_POST_SLUGS } from '../../lib/queries';
import Header from '../../components/Header';

interface Post {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  date: string;
  modified: string;
  slug: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText: string;
    };
  };
  author: {
    node: {
      name: string;
      slug: string;
    };
  };
  categories: {
    nodes: Array<{
      name: string;
      slug: string;
    }>;
  };
  tags: {
    nodes: Array<{
      name: string;
      slug: string;
    }>;
  };
}

interface PostPageProps {
  post: Post;
}

export default function PostPage({ post }: PostPageProps) {
  const phoneNumber = "(630) 715-8829";
  const companyName = "Partners Plumbing Co.";

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <Link href="/" className="text-slate-900 hover:text-slate-900">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const pageTitle = post.title;
  const pageDescription = post.excerpt;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
      </Head>

      <div className="min-h-screen bg-gray-50">
        <Header currentPage="/blog" phoneNumber={phoneNumber} companyName={companyName} />

        {/* Breadcrumb */}
        <nav className="bg-gray-50 py-3 border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Link href="/" className="hover:text-slate-900 transition-colors">Home</Link>
              <span>›</span>
              <Link href="/blog" className="hover:text-slate-900 transition-colors">Blog</Link>
              <span>›</span>
              <span className="text-slate-800 font-medium">{post.title}</span>
            </div>
          </div>
        </nav>

        {/* Article */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Featured Image */}
          {post.featuredImage && (
            <div className="mb-8">
              <img
                src={post.featuredImage.node.sourceUrl}
                alt={post.featuredImage.node.altText || post.title}
                className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          )}

          {/* Article Header */}
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>
            
            <div className="flex items-center text-gray-600 mb-6">
              <div className="flex items-center">
                <div>
                  <p className="font-medium">{post.author.node.name}</p>
                  <div className="flex items-center text-sm">
                    <time dateTime={post.date}>
                      Published {new Date(post.date).toLocaleDateString()}
                    </time>
                    {post.modified !== post.date && (
                      <>
                        <span className="mx-2">•</span>
                        <time dateTime={post.modified}>
                          Updated {new Date(post.modified).toLocaleDateString()}
                        </time>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Categories and Tags */}
            <div className="flex flex-wrap gap-4 mb-6">
              {post.categories.nodes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-gray-700">Categories:</span>
                  {post.categories.nodes.map((category) => (
                    <span
                      key={category.slug}
                      className="px-3 py-1 bg-slate-100 text-slate-800 text-sm rounded-full"
                    >
                      {category.name}
                    </span>
                  ))}
                </div>
              )}
              
              {post.tags.nodes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-gray-700">Tags:</span>
                  {post.tags.nodes.map((tag) => (
                    <span
                      key={tag.slug}
                      className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full"
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </header>

          {/* Article Content */}
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-12 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">{companyName}</h3>
                <p className="text-gray-300 mb-4">Professional plumbing services you can trust. Licensed, insured, and reliable.</p>
                <a href={`tel:${phoneNumber}`} className="text-yellow-400 font-bold text-lg hover:text-yellow-300 block mb-2">
                  {phoneNumber}
                </a>
                <a href="mailto:info@partnersinplumbing.com" className="text-gray-300 hover:text-white block mb-2">
                  info@partnersinplumbing.com
                </a>
                <p className="text-gray-300 text-sm">
                  Kane County, IL 60177
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/services/broken-pipe" className="hover:text-white">Broken Pipe Repair</Link></li>
                  <li><Link href="/services/leaky-plumbing" className="hover:text-white">Leak Repair</Link></li>
                  <li><Link href="/services/water-heater-replacement" className="hover:text-white">Water Heater Replacement</Link></li>
                  <li><Link href="/services/emergency-plumbing-service" className="hover:text-white">Emergency Plumbing</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/about" className="hover:text-white">About Us</Link></li>
                  <li><Link href="/blog" className="hover:text-white">Blog</Link></li>
                  <li><Link href="/contact" className="hover:text-white">Contact</Link></li>
                  <li><Link href="/services" className="hover:text-white">Services</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Service Areas</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>Kane County, Illinois</li>
                  <li>Geneva, Illinois</li>
                  <li>Saint Charles, Illinois</li>
                  <li>South Elgin, Illinois</li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 {companyName}. All rights reserved. | Licensed & Insured | Emergency Service Available</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const { data } = await client.query({
      query: GET_ALL_POST_SLUGS,
    });

    const paths = data.posts.nodes.map((post: { slug: string }) => ({
      params: { slug: post.slug },
    }));

    return {
      paths,
      fallback: 'blocking', // Enable ISR for new posts
    };
  } catch (error) {
    console.error('Error fetching post slugs:', error);
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const { data } = await client.query({
      query: GET_POST_BY_SLUG,
      variables: { slug: params?.slug },
    });

    if (!data || !data.postBy) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        post: data.postBy,
      },
      revalidate: 30, // Reduced from 60 seconds for faster updates
    };
  } catch (error) {
    console.error('Error fetching post:', error);
    return {
      notFound: true,
    };
  }
}; 